import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { useShallow } from "zustand/react/shallow";
import { api } from "@/services/api/client";
import { socketClient } from "@/services/websocket/socketClient";
import { toast } from "sonner";

/**
 * Invitation Store
 * Manages pending organization invitations for the authenticated user
 * Includes WebSocket real-time updates for invitation events
 */

// WebSocket event types for invitations (must match backend)
export const INVITATION_EVENTS = {
  INVITATION_RECEIVED: "invitation:received",
  INVITATION_CANCELLED: "invitation:cancelled",
  INVITATION_ACCEPTED: "invitation:accepted",
  INVITATION_DECLINED: "invitation:declined",
  MEMBER_JOINED: "organization:member-joined",
} as const;

// WebSocket payload types
export interface InvitationReceivedPayload {
  membershipId: string;
  organization: {
    _id: string;
    name: string;
    slug: string;
  };
  inviter: {
    name: string;
    email: string;
  };
  role: "admin" | "member" | "viewer";
  expiresAt: string;
  message?: string;
  provisioningConfig?: {
    homeProjectId: string | null;
    homePageId: string | null;
  } | null;
}

export interface InvitationCancelledPayload {
  membershipId: string;
  organization: {
    _id: string;
    name: string;
    slug: string;
  };
  cancelledBy: {
    name: string;
    email: string;
  };
}

export interface InvitationAcceptedPayload {
  membershipId: string;
  organization: {
    _id: string;
    name: string;
    slug: string;
  };
  acceptedBy: {
    userId: string;
    name: string;
    email: string;
  };
  role: string;
  acceptedAt: string;
}

export interface InvitationDeclinedPayload {
  membershipId: string;
  organization: {
    _id: string;
    name: string;
    slug: string;
  };
  declinedBy: {
    userId: string;
    name: string;
    email: string;
  };
  declinedAt: string;
}

export interface PendingInvitation {
  membershipId: string;
  organization: {
    _id: string;
    name: string;
    slug: string;
    description?: string;
    memberCount?: number;
  };
  inviter: {
    name: string;
    email: string;
    avatar?: string;
  } | null;
  role: "admin" | "member" | "viewer";
  invitedAt: string;
  expiresAt: string;
  message?: string;
  provisioningConfig?: {
    homeProjectId: string | null;
    homePageId: string | null;
  } | null;
}

export interface AcceptInvitationResult {
  organization?: {
    _id: string;
    name: string;
    slug: string;
  };
  role?: string;
  acceptedAt?: string;
  alreadyAccepted?: boolean;
  appliedProvisioningConfig?: {
    homeProjectId: string;
    homePageId: string | null;
  } | null;
  homepageProvisioningApplied?: boolean;
  redirectUrl?: string;
}

interface InvitationState {
  // State
  pendingInvitations: PendingInvitation[];
  pendingCount: number;
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;

  // Actions
  fetchPendingInvitations: () => Promise<void>;
  acceptInvitation: (membershipId: string) => Promise<AcceptInvitationResult>;
  declineInvitation: (membershipId: string) => Promise<void>;
  acceptByToken: (token: string) => Promise<AcceptInvitationResult>;
  declineByToken: (token: string) => Promise<void>;
  clearError: () => void;
  reset: () => void;

  // WebSocket event handlers (internal)
  _handleInvitationReceived: (payload: InvitationReceivedPayload) => void;
  _handleInvitationCancelled: (payload: InvitationCancelledPayload) => void;
}

// Cache duration: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;

const getAcceptInvitationResult = (
  data: AcceptInvitationResult | undefined
): AcceptInvitationResult => data ?? {};

export const useInvitationStore = create<InvitationState>()(
  subscribeWithSelector(
    immer((set, get) => ({
      // Initial state
      pendingInvitations: [],
      pendingCount: 0,
      isLoading: false,
      error: null,
      lastFetched: null,

      // Fetch pending invitations for the authenticated user
      fetchPendingInvitations: async () => {
        const { lastFetched, isLoading } = get();

        // Skip if already loading
        if (isLoading) {
          return;
        }

        // Skip if recently fetched (within cache duration)
        if (lastFetched && Date.now() - lastFetched < CACHE_DURATION) {
          return;
        }

        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const response = await api.get<PendingInvitation[]>("/invitations/user/pending");

          // Backend returns data as array directly, not { invitations: [...] }
          const invitations = Array.isArray(response.data) ? response.data : [];
          const count = invitations.length;

          set((state) => {
            state.pendingInvitations = invitations;
            state.pendingCount = count;
            state.isLoading = false;
            state.lastFetched = Date.now();
          });

        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to fetch pending invitations";

          console.error("[InvitationStore] Error fetching invitations:", errorMessage);

          set((state) => {
            state.error = errorMessage;
            state.isLoading = false;
          });
        }
      },

      // Accept invitation using membership ID (authenticated user)
      acceptInvitation: async (membershipId: string): Promise<AcceptInvitationResult> => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const response = await api.post<AcceptInvitationResult>(
            `/invitations/membership/${membershipId}/accept`
          );

          // Optimistically remove from pending list
          set((state) => {
            state.pendingInvitations = state.pendingInvitations.filter(
              (inv) => inv.membershipId !== membershipId
            );
            state.pendingCount = Math.max(0, state.pendingCount - 1);
            state.isLoading = false;
            state.lastFetched = null; // Invalidate cache
          });

          return getAcceptInvitationResult(response.data);
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to accept invitation";

          console.error("[InvitationStore] Error accepting invitation:", errorMessage);

          set((state) => {
            state.error = errorMessage;
            state.isLoading = false;
          });

          throw error;
        }
      },

      // Decline invitation using membership ID (authenticated user)
      declineInvitation: async (membershipId: string): Promise<void> => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          await api.post(`/invitations/membership/${membershipId}/decline`);

          // Optimistically remove from pending list
          set((state) => {
            state.pendingInvitations = state.pendingInvitations.filter(
              (inv) => inv.membershipId !== membershipId
            );
            state.pendingCount = Math.max(0, state.pendingCount - 1);
            state.isLoading = false;
            state.lastFetched = null; // Invalidate cache
          });

        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to decline invitation";

          console.error("[InvitationStore] Error declining invitation:", errorMessage);

          set((state) => {
            state.error = errorMessage;
            state.isLoading = false;
          });

          throw error;
        }
      },

      // Accept invitation using token (from email link)
      acceptByToken: async (token: string): Promise<AcceptInvitationResult> => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const response = await api.post<AcceptInvitationResult>(
            `/invitations/${token}/accept`
          );

          set((state) => {
            state.isLoading = false;
            state.lastFetched = null; // Invalidate cache
          });

          return getAcceptInvitationResult(response.data);
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to accept invitation";

          console.error("[InvitationStore] Error accepting by token:", errorMessage);

          set((state) => {
            state.error = errorMessage;
            state.isLoading = false;
          });

          throw error;
        }
      },

      // Decline invitation using token (from email link)
      declineByToken: async (token: string): Promise<void> => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          await api.post(`/invitations/${token}/decline`);

          set((state) => {
            state.isLoading = false;
            state.lastFetched = null; // Invalidate cache
          });

        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to decline invitation";

          console.error("[InvitationStore] Error declining by token:", errorMessage);

          set((state) => {
            state.error = errorMessage;
            state.isLoading = false;
          });

          throw error;
        }
      },

      // Clear error state
      clearError: () => {
        set((state) => {
          state.error = null;
        });
      },

      // Reset store (on logout)
      reset: () => {
        set((state) => {
          state.pendingInvitations = [];
          state.pendingCount = 0;
          state.isLoading = false;
          state.error = null;
          state.lastFetched = null;
        });
      },

      // WebSocket event handler: New invitation received
      _handleInvitationReceived: (payload: InvitationReceivedPayload) => {
        set((state) => {
          // Check if invitation already exists (prevent duplicates)
          const exists = state.pendingInvitations.some(
            (inv) => inv.membershipId === payload.membershipId
          );

          if (!exists) {
            const newInvitation: PendingInvitation = {
              membershipId: payload.membershipId,
              organization: {
                _id: payload.organization._id,
                name: payload.organization.name,
                slug: payload.organization.slug,
              },
              inviter: payload.inviter
                ? {
                    name: payload.inviter.name,
                    email: payload.inviter.email,
                  }
                : null,
              role: payload.role,
              invitedAt: new Date().toISOString(),
              expiresAt: payload.expiresAt,
              message: payload.message,
              provisioningConfig: payload.provisioningConfig ?? null,
            };

            state.pendingInvitations.unshift(newInvitation);
            state.pendingCount = state.pendingInvitations.length;
            state.lastFetched = null; // Invalidate cache
          }
        });

        // Show toast notification
        toast.info(`New invitation from ${payload.organization.name}`, {
          description: `${payload.inviter?.name ?? "Someone"} invited you to join as ${payload.role}`,
          action: {
            label: "View",
            onClick: () => {
              // Navigate to dashboard or invitations page
              window.location.href = "/dashboard";
            },
          },
        });
      },

      // WebSocket event handler: Invitation cancelled
      _handleInvitationCancelled: (payload: InvitationCancelledPayload) => {
        set((state) => {
          state.pendingInvitations = state.pendingInvitations.filter(
            (inv) => inv.membershipId !== payload.membershipId
          );
          state.pendingCount = state.pendingInvitations.length;
          state.lastFetched = null; // Invalidate cache
        });

        // Show toast notification
        toast.info(`Invitation cancelled`, {
          description: `${payload.cancelledBy.name} cancelled your invitation to ${payload.organization.name}`,
        });
      },
    }))
  )
);

// Subscribe to auth store to reset invitation store on logout
if (typeof window !== "undefined") {
  import("../auth/auth-store").then(({ useAuthStore }) => {
    useAuthStore.subscribe(
      (state) => state.isAuthenticated,
      (isAuthenticated, previousIsAuthenticated) => {
        // If user logged out, reset invitation state
        if (previousIsAuthenticated && !isAuthenticated) {
          useInvitationStore.getState().reset();
        }
        // If user logged in, fetch pending invitations
        else if (!previousIsAuthenticated && isAuthenticated) {
          // Small delay to allow auth to fully complete
          setTimeout(() => {
            useInvitationStore.getState().fetchPendingInvitations();
          }, 500);
        }
      }
    );
  });
}

// Set up WebSocket listeners for real-time invitation events
let wsListenersRegistered = false;

export const setupInvitationWebSocketListeners = (): void => {
  if (wsListenersRegistered) {
    return;
  }

  if (!socketClient.isConnected()) {
    return;
  }

  // Register listener for invitation received
  socketClient.onCustomEvent(
    INVITATION_EVENTS.INVITATION_RECEIVED,
    (payload: unknown) => {
      useInvitationStore
        .getState()
        ._handleInvitationReceived(payload as InvitationReceivedPayload);
    }
  );

  // Register listener for invitation cancelled
  socketClient.onCustomEvent(
    INVITATION_EVENTS.INVITATION_CANCELLED,
    (payload: unknown) => {
      useInvitationStore
        .getState()
        ._handleInvitationCancelled(payload as InvitationCancelledPayload);
    }
  );

  wsListenersRegistered = true;
};

// Clean up WebSocket listeners
export const cleanupInvitationWebSocketListeners = (): void => {
  if (!wsListenersRegistered) {
    return;
  }

  socketClient.offCustomEvent(INVITATION_EVENTS.INVITATION_RECEIVED);
  socketClient.offCustomEvent(INVITATION_EVENTS.INVITATION_CANCELLED);

  wsListenersRegistered = false;
};

// Auto-setup WebSocket listeners when socket connects
if (typeof window !== "undefined") {
  // Try to setup immediately if socket is already connected
  setTimeout(() => {
    if (socketClient.isConnected()) {
      setupInvitationWebSocketListeners();
    }
  }, 1000);

  // Also setup when socket connects later
  socketClient.on("connected", () => {
    setupInvitationWebSocketListeners();
  });
}

// Export selector hooks for common use cases
export const usePendingInvitations = () =>
  useInvitationStore((state) => state.pendingInvitations);

export const usePendingCount = () =>
  useInvitationStore((state) => state.pendingCount);

export const useInvitationActions = () =>
  useInvitationStore(
    useShallow((state) => ({
      fetchPendingInvitations: state.fetchPendingInvitations,
      acceptInvitation: state.acceptInvitation,
      declineInvitation: state.declineInvitation,
      acceptByToken: state.acceptByToken,
      declineByToken: state.declineByToken,
      clearError: state.clearError,
    }))
  );
